import { useEffect, useState } from 'react';
import { useConfirmMapping, useImportPreview } from '@/lib/api/imports';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';

const FIELD_OPTIONS = [
  'sku',
  'name',
  'description',
  'brand',
  'category',
  'price',
  'currency',
  'status',
];

type TargetKind = 'ignore' | 'field' | 'attribute';

function kindOf(target: string): TargetKind {
  if (target === 'ignore' || target === '') return 'ignore';
  if (target.startsWith('attribute:')) return 'attribute';
  return 'field';
}

function MappingTargetSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const kind = kindOf(value);
  const attributeKey = kind === 'attribute' ? value.slice('attribute:'.length) : '';

  return (
    <div className="flex flex-1 items-center gap-2">
      <Select
        value={kind === 'field' ? value.replace('field:', '') : kind}
        onChange={(e) => {
          const v = e.target.value;
          if (v === 'ignore') onChange('ignore');
          else if (v === 'attribute') onChange('attribute:');
          else onChange(`field:${v}`);
        }}
        data-testid="mapping-target-select"
      >
        <option value="ignore">Ignore</option>
        {FIELD_OPTIONS.map((f) => (
          <option key={f} value={f}>
            {f}
          </option>
        ))}
        <option value="attribute">Attribute...</option>
      </Select>
      {kind === 'attribute' && (
        <Input
          value={attributeKey}
          onChange={(e) => onChange(`attribute:${e.target.value}`)}
          placeholder="attribute key"
          data-testid="mapping-attribute-key-input"
        />
      )}
    </div>
  );
}

interface ImportMappingEditorProps {
  catalogId: string;
  jobId: string;
}

/** Preview/mapping-confirm screen for a job awaiting_mapping (AC-1, AC-9). */
export function ImportMappingEditor({ catalogId, jobId }: ImportMappingEditorProps) {
  const preview = useImportPreview(jobId);
  const confirm = useConfirmMapping(catalogId, jobId);
  const [targets, setTargets] = useState<Record<string, string>>({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!preview.data || loaded) return;
    const initial: Record<string, string> = {};
    for (const header of preview.data.headers) {
      initial[header] = preview.data.suggested_mapping[header]?.target ?? 'ignore';
    }
    setTargets(initial);
    setLoaded(true);
  }, [preview.data, loaded]);

  if (preview.isLoading || !preview.data) {
    return <Spinner label="Loading preview" />;
  }

  function handleConfirm() {
    const mapping: Record<string, { target: string }> = {};
    for (const [header, target] of Object.entries(targets)) {
      mapping[header] = { target };
    }
    confirm.mutate(mapping);
  }

  return (
    <div data-testid="import-mapping-editor" className="space-y-3">
      <p className="text-sm text-gray-600">{preview.data.total_rows} rows detected.</p>

      <div className="space-y-2">
        {preview.data.headers.map((header) => (
          <div
            key={header}
            className="flex items-center gap-2"
            data-testid="mapping-row"
            data-column={header}
          >
            <span className="w-40 shrink-0 truncate text-sm text-gray-900">{header}</span>
            <MappingTargetSelect
              value={targets[header] ?? 'ignore'}
              onChange={(v) => setTargets((t) => ({ ...t, [header]: v }))}
            />
          </div>
        ))}
      </div>

      <div className="overflow-x-auto rounded-md border border-gray-200">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="border-b border-gray-200 text-gray-500">
              {preview.data.headers.map((h) => (
                <th key={h} className="whitespace-nowrap px-2 py-1">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {preview.data.sample_rows.slice(0, 10).map((row, i) => (
              <tr key={i}>
                {row.map((v, j) => (
                  <td key={j} className="whitespace-nowrap px-2 py-1 text-gray-700">
                    {v === null || v === undefined ? '' : String(v)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {confirm.error && (
        <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {(confirm.error as Error).message}
        </p>
      )}

      <Button
        type="button"
        onClick={handleConfirm}
        disabled={confirm.isPending}
        data-testid="confirm-mapping-button"
      >
        {confirm.isPending ? 'Applying...' : 'Confirm and apply'}
      </Button>
    </div>
  );
}
